import React, { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Heading,
    Box,
    List,
    ListItem,
    Text,
    Flex,
    useToast,
    Spinner,
    IconButton,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";

const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

const ReadOnlyRequest = () => {
    const [request, setRequest] = useState([]);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    
    // Parameters necessary for making api requests
    const { id } = useParams();
    const HuserID = localStorage.getItem("HuserID");
    const UUID = localStorage.getItem("UUID");

    // Fetching header data for selected Request:
    const fetchRequest = async () => {
        try {
            setLoading(true);
            let url = `${API_BASE_URL}?method=getRequestHeaders&huserID=${HuserID}&uuid=${UUID}&HrequestID=${id}`;

            const response = await fetch(url, {
                method: "GET",
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setRequest(data[0]);
        } catch (error) {
            console.error("Error fetching request:", error);
            toast({
                title: "Error fetching request details",
                description: error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetching Reports For Request to populate report listing table
    const getReportsPerRequest = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${API_BASE_URL}?method=getReportsPerRequest&huserID=${HuserID}&uuid=${UUID}&HrequestID=${id}`,
                {
                    method: "GET",
                }
            );

            const data = await response.json();
            if (data.length) {
                setReports(data);
            } else {
                setReports([]);
            }
        } catch (error) {
            console.error("Error fetching reports:", error);
            toast({
                title: "Error fetching reports",
                description: error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequest();
        getReportsPerRequest();
    }, []);

    // Handle download report
    const handleDownload = (url) => {
        if (url) {
            window.open(url, '_blank');
        } else {
            toast({
                title: "Download Error",
                description: "No download link available",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Loading spinner
    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="30vh"
            >
                <Spinner size="xl" />
            </Box>
        );
    }

    return (
        <Box p={8}>
            {/* Request Details page heading */}
            <Heading className="text-xl font-bold mb-4" mb={5}>
                Request Details
            </Heading>

            {/* Request Headers */}
            <Table variant="simple" mb={16}>
                <Thead>
                    <Tr>
                        <Th>Date Recorded</Th>
                        <Th>Client Name</Th>
                        <Th>Candidate Name</Th>
                        <Th>Battery Name</Th>
                        <Th>Operational Department</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    <Tr>
                        <Td>{request.dateRecorded}</Td>
                        <Td>{request.clientName}</Td>
                        <Td>{request.candFname + " " + request.candSname}</Td>
                        <Td>{request.batteryName}</Td>
                        <Td>{request.tdepartment}</Td>
                    </Tr>
                </Tbody>
                <Thead>
                    <Tr>
                        <Th>Purpose</Th>
                        <Th>Current Position</Th>
                        <Th>Position Applied For</Th>
                        <Th>Employment Type</Th>
                        <Th>Current Status</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    <Tr>
                        <Td>{request.purpose}</Td>
                        <Td>{request.currentPosition}</Td>
                        <Td>{request.positionAppliedFor}</Td>
                        <Td>{request.tpositionType}</Td>
                        <Td>{request.statusText}</Td>
                    </Tr>
                </Tbody>
            </Table>

            {/* Report Listing */}
            <Box>
                <Heading size="md" mb={4}>Reports</Heading>
                <List>
                    <ListItem
                        border="2px"
                        borderColor="gray.100"
                        borderTopRadius="md"
                    >
                        <Flex>
                            <Text w="30%" fontWeight="bold" p="2">
                                Date
                            </Text>
                            <Text w="10%" fontWeight="bold" p="2">
                                Download Link
                            </Text>
                            <Text w="30%" fontWeight="bold" p="2">
                                Report Name
                            </Text>
                            <Text w="30%" fontWeight="bold" p="2">
                                Uploaded By
                            </Text>
                        </Flex>
                    </ListItem>

                    {reports.length > 0 ? (
                        reports.map((report, index) => (
                            <ListItem
                                key={`report-${index}`}
                                borderBottom="2px"
                                borderLeft="2px"
                                borderRight="2px"
                                borderColor="gray.100"
                                borderBottomRadius="md"
                            >
                                <Flex>
                                    <Text w="30%" variant={"tableBodyCell"} p="2">
                                        {report.dateon}
                                    </Text>
                                    <Box w="10%" p="2">
                                        <IconButton
                                            icon={<DownloadIcon />}
                                            onClick={() =>
                                                handleDownload(report.downLoadLink)
                                            }
                                            aria-label="Download report"
                                        />
                                    </Box>
                                    <Text w="30%" variant={"tableBodyCell"} p="2">
                                        {report.reportName}
                                    </Text>
                                    <Text w="30%" variant={"tableBodyCell"} p="2">
                                        {report.uploadedBy}
                                    </Text>
                                </Flex>
                            </ListItem>
                        ))
                    ) : (
                        <ListItem
                            borderBottom="2px"
                            borderLeft="2px"
                            borderRight="2px"
                            borderColor="gray.100"
                            borderBottomRadius="md"
                        >
                            <Flex>
                                <Text w="100%" p="4" textAlign="center" color="gray.500">
                                    No reports available for this request
                                </Text>
                            </Flex>
                        </ListItem>
                    )}
                </List>
            </Box>
        </Box>
    );
};

export default ReadOnlyRequest; 